/**
 * Login Page
 * Split-screen layout with email/password form
 * Per design guidelines: Primary blue (#2563eb), white form background
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/auth/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail } from '../utils/validation';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset errors
    setErrors({});
    setFormError('');

    // Validation
    const newErrors: { email?: string; password?: string } = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit
    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);

    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setFormError(result.error ?? 'Login failed');
    }
  };

  return (
    <AuthLayout showSidePanel={true} sidePanelVariant="login">
      <Card className="w-full border-slate-200 shadow-lg rounded-2xl" data-testid="login-card">
        <CardHeader className="space-y-1 pb-6">
          <CardTitle
            className="text-2xl sm:text-3xl font-semibold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Sign in to your account
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            Enter your credentials to access UserLife JML
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Form Error */}
          {formError && (
            <Alert variant="destructive" className="mb-6" data-testid="form-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Authentication Failed</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                className="h-11 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                data-testid="email-input"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p
                  id="email-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="email-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="password-input"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  data-testid="password-toggle-button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p
                  id="password-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="password-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(value: boolean | 'indeterminate') =>
                    setRememberMe(value === true)
                  }
                  className="border-slate-300 data-[state=checked]:bg-primary-600 data-[state=checked]:border-primary-600"
                  data-testid="remember-me-checkbox"
                />
                <label
                  htmlFor="remember"
                  className="text-sm font-medium text-slate-700 cursor-pointer"
                >
                  Remember me
                </label>
              </div>

              <Link
                to="/app/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors duration-200"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              data-testid="submit-button"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6">
            <p className="text-center text-sm text-slate-600">
              Don&apos;t have an account?{' '}
              <Link
                to="/app/register"
                className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                data-testid="register-link"
              >
                Sign up
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default Login;
